import { Component } from '@theme/component';

const DEBOUNCE_MS = 300;

/**
 * @typedef {Object} StorePredictiveSearchRefs
 * @property {HTMLInputElement} input
 * @property {HTMLElement} results
 * @property {HTMLElement} status
 */

/**
 * Header search preview. Fetches the store-predictive-search section as the merchant types.
 * @extends {Component<StorePredictiveSearchRefs>}
 */
class StorePredictiveSearch extends Component {
  requiredRefs = ['input', 'results', 'status'];

  /** @type {AbortController | null} */
  #lifecycle = null;

  /** @type {AbortController | null} */
  #request = null;

  /** @type {ReturnType<typeof setTimeout> | number} */
  #debounce = 0;

  /** @type {Record<string, string>} */
  #cache = {};

  connectedCallback() {
    super.connectedCallback();
    this.#lifecycle?.abort();
    this.#lifecycle = new AbortController();
    const { signal } = this.#lifecycle;
    const form = this.querySelector('form');

    this.refs.input.addEventListener('input', () => this.#onInput(), { signal });
    this.refs.input.addEventListener('focus', () => this.#onFocus(), { signal });
    this.addEventListener('focusout', () => this.#onFocusOut(), { signal });
    this.addEventListener('keydown', (event) => this.#onKeydown(event), { signal });
    form?.addEventListener('submit', (event) => this.#onSubmit(event), { signal });
  }

  disconnectedCallback() {
    this.#lifecycle?.abort();
    this.#lifecycle = null;
    this.#request?.abort();
    this.#request = null;
    clearTimeout(this.#debounce);
    super.disconnectedCallback();
  }

  #query() {
    return this.refs.input.value.trim();
  }

  #onInput() {
    clearTimeout(this.#debounce);
    this.#debounce = setTimeout(() => {
      this.#search();
    }, DEBOUNCE_MS);
  }

  #onFocus() {
    const query = this.#query();
    if (!query) return;

    if (this.hasAttribute('results')) {
      this.#open();
      return;
    }

    this.#search();
  }

  #onFocusOut() {
    requestAnimationFrame(() => {
      if (!this.contains(document.activeElement)) this.#close();
    });
  }

  #onSubmit(event) {
    if (!this.#query() || this.querySelector('[aria-selected="true"] a')) {
      event.preventDefault();
    }
  }

  /**
   * @param {KeyboardEvent} event
   */
  #onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
      this.#switchOption(event.code === 'ArrowUp' ? 'up' : 'down');
      return;
    }

    if (event.code === 'Enter') {
      this.#selectOption();
      return;
    }

    if (event.code === 'Escape') this.#close();
  }

  /**
   * @returns {HTMLElement[]}
   */
  #options() {
    return Array.from(this.refs.results.querySelectorAll('[role="option"]'));
  }

  /**
   * @param {'up' | 'down'} direction
   */
  #switchOption(direction) {
    if (!this.hasAttribute('open')) return;

    const options = this.#options();
    if (!options.length) return;

    const selected = this.refs.results.querySelector('[aria-selected="true"]');
    const index = selected ? options.indexOf(selected) : -1;
    let nextIndex = direction === 'down' ? index + 1 : index - 1;
    if (nextIndex < 0) nextIndex = options.length - 1;
    if (nextIndex >= options.length) nextIndex = 0;

    selected?.setAttribute('aria-selected', 'false');
    const next = options[nextIndex];
    next.setAttribute('aria-selected', 'true');
    this.refs.input.setAttribute('aria-activedescendant', next.id);
    this.#setStatus(next.textContent?.trim() ?? '');
  }

  #selectOption() {
    const selected = this.querySelector('[aria-selected="true"] a, [aria-selected="true"] button');
    if (selected instanceof HTMLElement) selected.click();
  }

  async #search() {
    const query = this.#query();
    if (!query) {
      this.#close(true);
      return;
    }

    const key = query.toLowerCase();
    this.#setStatus(this.dataset.loadingText || '');
    this.setAttribute('loading', '');

    if (this.#cache[key]) {
      this.#render(this.#cache[key]);
      return;
    }

    this.#request?.abort();
    this.#request = new AbortController();
    const { signal } = this.#request;
    const url = new URL(this.dataset.suggestUrl || '/search/suggest', window.location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('resources[type]', this.dataset.types || 'product');
    url.searchParams.set('resources[limit]', this.dataset.limit || '4');
    url.searchParams.set('options[prefix]', 'last');
    url.searchParams.set('options[unavailable_products]', this.dataset.unavailable || 'last');
    url.searchParams.set('section_id', this.dataset.section || 'store-predictive-search');

    try {
      const response = await fetch(url.toString(), { signal });
      if (!response.ok) throw new Error(String(response.status));

      const text = await response.text();
      const doc = new DOMParser().parseFromString(text, 'text/html');
      const section = doc.querySelector('#shopify-section-store-predictive-search');
      const markup = section?.innerHTML ?? '';
      this.#cache[key] = markup;
      this.#render(markup);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      this.#close();
    }
  }

  /**
   * @param {string} markup
   */
  #render(markup) {
    this.refs.results.innerHTML = markup;
    this.setAttribute('results', '');
    this.removeAttribute('loading');
    const count = this.refs.results.querySelector('[data-predictive-search-live-region-count-value]');
    this.#setStatus(count?.textContent?.trim() ?? '');
    this.#open();
  }

  /**
   * @param {string} text
   */
  #setStatus(text) {
    this.refs.status.textContent = text;
    this.refs.status.setAttribute('aria-hidden', 'false');
    setTimeout(() => {
      this.refs.status.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  #open() {
    const headerGroup = this.closest('#header-group') || document.getElementById('header-group');
    const bottom = (headerGroup || this).getBoundingClientRect().bottom;
    this.refs.results.style.maxHeight = `${Math.max(160, window.innerHeight - bottom)}px`;
    this.setAttribute('open', '');
    this.refs.input.setAttribute('aria-expanded', 'true');
  }

  /**
   * @param {boolean} [clear]
   */
  #close(clear = false) {
    if (clear) {
      this.refs.input.value = '';
      this.removeAttribute('results');
    }

    this.querySelector('[aria-selected="true"]')?.setAttribute('aria-selected', 'false');
    this.refs.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('open');
    this.removeAttribute('loading');
    this.refs.input.setAttribute('aria-expanded', 'false');
    this.refs.results.removeAttribute('style');
  }
}

if (!customElements.get('store-predictive-search')) {
  customElements.define('store-predictive-search', StorePredictiveSearch);
}
